import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5roaqutc768qf4bqm60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="bg-[#1f2937] py-16 sm:py-24 lg:py-32 px-4 sm:px-8">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-20">
          {/* Left Side - Info */}
          <div>
            <div className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-wide text-[#718096] mb-4 sm:mb-6">
              <span className="w-2 h-2 bg-[#8B6914] rounded-full"></span>
              GET IN TOUCH
            </div>

            <h2 className="text-[40px] sm:text-[56px] lg:text-[64px] font-serif text-[#F7F7F7] leading-tight mb-6 sm:mb-8">
              Contact the Forge
            </h2>

            <p className="text-base sm:text-lg text-[#A0AEC0] leading-relaxed mb-8 sm:mb-12">
              Have questions about our weapons, custom orders, or craftsmanship? Reach out to us directly. We're here to help you find the perfect weapon for your needs.
            </p>

            <div className="space-y-6 sm:space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#8B6914] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#F7F7F7] mb-1">Email Us</h3>
                  <p className="text-sm sm:text-base text-[#A0AEC0] break-all">contact@darkwarriorweapons.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#8B6914] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#F7F7F7] mb-1">Response Time</h3>
                  <p className="text-sm sm:text-base text-[#A0AEC0]">We typically respond within 24-48 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#8B6914] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-lg sm:text-xl"></i>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-[#F7F7F7] mb-1">Legal & Safety</h3>
                  <p className="text-sm sm:text-base text-[#A0AEC0]">All weapons comply with local laws. Responsible ownership required.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-[#2d3748] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10">
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-5 sm:space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#F7F7F7] mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#1f2937] border-2 border-[#4A5568] rounded-xl px-4 py-3 text-sm sm:text-base text-white focus:border-[#8B6914] focus:outline-none transition-colors"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#F7F7F7] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#1f2937] border-2 border-[#4A5568] rounded-xl px-4 py-3 text-sm sm:text-base text-white focus:border-[#8B6914] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-semibold text-[#F7F7F7] mb-2">
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full bg-[#1f2937] border-2 border-[#4A5568] rounded-xl px-4 py-3 text-sm sm:text-base text-white focus:border-[#8B6914] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="">Select a subject</option>
                  <option value="Product Inquiry">Product Inquiry</option>
                  <option value="Custom Order">Custom Order</option>
                  <option value="General Question">General Question</option>
                  <option value="Shipping & Returns">Shipping & Returns</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#F7F7F7] mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full bg-[#1f2937] border-2 border-[#4A5568] rounded-xl px-4 py-3 text-sm sm:text-base text-white focus:border-[#8B6914] focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your inquiry..."
                ></textarea>
                <p className="text-xs text-[#718096] mt-2">Maximum 500 characters</p>
              </div>

              {status === 'success' && (
                <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-xl text-sm">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl text-sm">
                  Failed to send message. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-[#8B6914] text-white py-3 sm:py-4 rounded-xl text-sm sm:text-base font-bold hover:bg-[#6B4423] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {status === 'submitting' ? 'SENDING...' : 'SEND MESSAGE'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
